import { GET_WAN, CREATE_WAN, DELETE_WAN, UPDATE_WAN, GET_WAN_SITES, GET_WANS_BY_GATEWAY, GET_WAN_WITH_DHCP, ALL_WANS } from './gql.queries';
import { gql } from '@apollo/client';
import { UPDATE_SITE, DELETE_LINK } from '@app/api/site/gql.queries';
import { UPDATE_TUNNEL, CREATE_TUNNEL, DELETE_TUNNEL } from '@app/api/tunnel/gql.queries';
import Site from '@app/api/site/site.actions';
// Use Gateway alias since there is a Server var already present
import { default as Gateway } from '@app/api/gateway/gateway.actions';
import UserSession from '@app/common/user-session';

import { apolloClient } from '../graphql/client';
import { GQLHelpers } from '../hasura/ts';
import { tokenIsValid } from '@app/lib/utils';
import { cognitoUser } from '@app/lib/cognito';
import { store } from '@app/store';

export default {
  async getAll() {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      const partnerId = UserSession.getPartnerId();
      
      // Use direct GraphQL query instead of fragment helper to get DHCP data
      try {
        const { data } = await apolloClient.query({
          query: ALL_WANS,
          variables: { partnerId },
        });
        
        const controllersById = new Map((data.Gateway || []).map(controller => [controller.id, controller]));
        const serversById = new Map((data.Server || []).map(server => [server.id, server]));
        const Wans = (data.Wan || []).map(wan => ({
          ...wan,
          activeController: controllersById.get(wan.activeGatewayId) || null,
          standbyController: controllersById.get(wan.standbyGatewayId) || null,
          activeGateway: serversById.get(wan.activeServerId) || null,
          standbyGateway: serversById.get(wan.standbyServerId) || null,
        }));
        
        return Wans;
      } catch (error) {
        throw error;
      }
    }
    else {
      cognitoUser.signOut();
      return null;
    }
  },

  async getWansByController(gatewayId) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      const { objects: Wans } = await GQLHelpers.Fragments.Wan.queryObjects({
        apolloClient,
        options: {
          variables: {
            where: { _or: [{ activeGatewayId: { _eq: gatewayId } }, { standbyGatewayId: { _eq: gatewayId } }] },
          },
        },
      });
      return Wans;
    }
    else {
      cognitoUser.signOut();
      return null;
    }
  },

  async getWanSites(wanId) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      const { data: sites } = await apolloClient.query({
        query: GET_WAN_SITES,
        variables: { wanId: wanId },
      });
      return sites;
    }
    else {
      cognitoUser.signOut();
    }
  },

  async get(wanId: string) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      const { data } = await apolloClient.query({
        query: GET_WAN,
        variables: { id: wanId },
      });
      return data.Wan?.[0] || null;
    }
    else {
      cognitoUser.signOut();
      return null;
    }
  },

  async getWithDhcp(wanId: string) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      
             try {
         const { data } = await apolloClient.query({
           query: GET_WAN_WITH_DHCP,
           variables: { id: wanId },
         });
         
         const wan = data.Wan[0] || null;
         const dhcpConfigs = data.dhcp_configurations || wan?.DHCP || [];
         
         if (wan) {
           // The form consumes one configuration while Hasura returns the relationship as an array.
           wan.DHCP = dhcpConfigs[0] || null;
         }
         
         return wan;
       } catch (error) {
         throw error;
      }
    }
    else {
      cognitoUser.signOut();
      return null;
    }
  },

  async delete(wanId: string) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      await GQLHelpers.Models.Wan.removeById({ apolloClient, wanId });
    }
    else {
      cognitoUser.signOut();
    }
  },

  async create(params) {
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      try {
        const partnerId = UserSession.getPartnerId();
        params.partnerId = partnerId;
        const { siteSubnets, ...wanParams } = params;
        

        const supportedFields = ['activeGatewayId', 'activeServerId', 'created_at', 'description', 'id', 'isEncrypted', 'name', 'partnerId', 'standbyGatewayId', 'standbyServerId', 'updated_at', 'type'];
        const filteredParams = {};
        
        const unsupportedFields = Object.keys(wanParams).filter(field => !supportedFields.includes(field));
        
        supportedFields.forEach(field => {
          if (wanParams[field] !== undefined) {
            filteredParams[field] = wanParams[field];
          }
        });
        

        

        
        const result = await apolloClient.mutate({
          mutation: CREATE_WAN,
          variables: { input: [filteredParams] },
        });
        

        
        const {
          data: {
            insert_Wan: {
              returning: [wan],
            },
          },
        } = result;
        

        if (wanParams.DHCP) {
          const dhcpResult = await apolloClient.mutate({
            mutation: gql`
              mutation InsertDhcpConfigurations($description: String, $partner_id: uuid, $resource_id: uuid, $subnet: cidr, $range_start: inet, $range_end: inet, $default_lease_seconds: Int, $max_lease_seconds: Int) {
                insert_dhcp_configurations(objects: {
                  description: $description, 
                  partner_id: $partner_id, 
                  resource_id: $resource_id,
                  subnet: $subnet,
                  range_start: $range_start,
                  range_end: $range_end,
                  default_lease_seconds: $default_lease_seconds,
                  max_lease_seconds: $max_lease_seconds
                }) {
                  affected_rows
                  returning {
                    id
                    subnet
                    range_end
                    range_start
                    default_lease_seconds
                    max_lease_seconds
                    description
                    resource_id
                  }
                }
              }
            `,
            variables: {
              description: wanParams.DHCP.description,
              partner_id: partnerId,
              resource_id: wan.id,
              subnet: wanParams.DHCP.subnet,
              range_start: wanParams.DHCP.range_start,
              range_end: wanParams.DHCP.range_end,
              default_lease_seconds: wanParams.DHCP.default_lease_seconds,
              max_lease_seconds: wanParams.DHCP.max_lease_seconds
            }
          });
          

        }
        
        return wan;
      } catch (error) {


        throw error;
      }
    }
    else {
      cognitoUser.signOut();
    }
  },

  async update(id, changes, sites) {

    
    const state = store.getState();
    const expiration = state.common.auth.session?.expiration ?? 0;

    if (tokenIsValid(expiration)) {
      const { siteSubnets, DHCP, asn, vxlan_id, IPCIDR, ...wanChanges } = changes;
      


       const supportedFields = ['activeGatewayId', 'activeServerId', 'created_at', 'description', 'id', 'isEncrypted', 'name', 'partnerId', 'standbyGatewayId', 'standbyServerId', 'updated_at', 'type'];
      const filteredWanChanges = {};
      
      supportedFields.forEach(field => {
        if (wanChanges[field] !== undefined) {
          filteredWanChanges[field] = wanChanges[field];
        }
      });
      

      


      const { data: wanUpdateData } = await apolloClient.mutate({
        mutation: UPDATE_WAN,
        variables: { id, changes: filteredWanChanges },
      });

      if (!wanUpdateData?.update_Wan?.affected_rows) {
        throw new Error(`Unable to update pWAN ${id}`);
      }
      

      if (DHCP) {

        try {
          const partnerId = UserSession.getPartnerId();
          

          const existingDhcpQuery = await apolloClient.query({
            query: gql`
              query GetDhcpForWan($resource_id: uuid!) {
                dhcp_configurations(where: {resource_id: {_eq: $resource_id}}) {
                  id
                }
              }
            `,
            variables: { resource_id: id }
          });
          
          if (existingDhcpQuery.data.dhcp_configurations.length > 0) {
            // Update existing DHCP configuration
            const dhcpId = existingDhcpQuery.data.dhcp_configurations[0].id;

            
            await apolloClient.mutate({
              mutation: gql`
                mutation UpdateDhcpConfiguration($id: uuid!, $description: String, $subnet: cidr, $range_start: inet, $range_end: inet, $default_lease_seconds: Int, $max_lease_seconds: Int) {
                  update_dhcp_configurations_by_pk(pk_columns: {id: $id}, _set: {
                    description: $description,
                    subnet: $subnet,
                    range_start: $range_start,
                    range_end: $range_end,
                    default_lease_seconds: $default_lease_seconds,
                    max_lease_seconds: $max_lease_seconds
                  }) {
                    id
                  }
                }
              `,
              variables: {
                id: dhcpId,
                description: DHCP.description,
                subnet: DHCP.subnet,
                range_start: DHCP.range_start,
                range_end: DHCP.range_end,
                default_lease_seconds: DHCP.default_lease_seconds,
                max_lease_seconds: DHCP.max_lease_seconds
              }
            });

          } else {


            
            await apolloClient.mutate({
              mutation: gql`
                mutation InsertDhcpConfigurations($description: String, $partner_id: uuid, $resource_id: uuid, $subnet: cidr, $range_start: inet, $range_end: inet, $default_lease_seconds: Int, $max_lease_seconds: Int) {
                  insert_dhcp_configurations(objects: {
                    description: $description, 
                    partner_id: $partner_id, 
                    resource_id: $resource_id,
                    subnet: $subnet,
                    range_start: $range_start,
                    range_end: $range_end,
                    default_lease_seconds: $default_lease_seconds,
                    max_lease_seconds: $max_lease_seconds
                  }) {
                    affected_rows
                    returning {
                      id
                    }
                  }
                }
              `,
              variables: {
                description: DHCP.description,
                partner_id: partnerId,
                resource_id: id,
                subnet: DHCP.subnet,
                range_start: DHCP.range_start,
                range_end: DHCP.range_end,
                default_lease_seconds: DHCP.default_lease_seconds,
                max_lease_seconds: DHCP.max_lease_seconds
              }
            });

          }
        } catch (dhcpError) {

        }
      }
  
      const sitePromises = (siteSubnets || []).map(async (siteSubnet, index) => {
        // TODO: This layer should not need to know about the structure of site.cells[x].props.value
        const siteId = siteSubnet.cells[4].props.value;
        const ipAccessLevel = siteSubnet.cells[2].props.value;
        const accessibleIps = siteSubnet.cells[3].props.value;
        const origSite = sites.find((site) => site.id === siteId);

        if (!origSite) {
          throw new Error(`Unable to find site ${siteId} while updating pWAN ${id}`);
        }

        const primaryTunnel = origSite.Tunnels?.[0];
        if (!primaryTunnel) {
          throw new Error(`Site ${siteId} has no primary tunnel`);
        }
  
        const addingFailover = origSite.Tunnels[1] === undefined && Boolean(wanChanges.standbyServerId);
        const removingFailover = origSite.Tunnels[1] !== undefined && !wanChanges.standbyServerId;
        const activeTunnelServerChanged = primaryTunnel.serverId !== wanChanges.activeServerId;
        const standbyTunnelServerChanged =
          origSite.Tunnels[1] && wanChanges.standbyServerId
            ? origSite.Tunnels[1].serverId !== wanChanges.standbyServerId
            : false;

        const encryptionChanged = primaryTunnel.isClearTextData === wanChanges.isEncrypted;
        const ipAccessLevelChanged = origSite.ipAccessLevel !== ipAccessLevel;
        const accessibleIpsChanged = origSite.accessibleIps !== accessibleIps;
        const siteLevelAccessChanged = ipAccessLevelChanged || accessibleIpsChanged;
        const existingTunnelsNeedUpdate = activeTunnelServerChanged || standbyTunnelServerChanged || encryptionChanged || siteLevelAccessChanged;
  
        // Update Tunnels
        for (let i = 0; existingTunnelsNeedUpdate && i < origSite.Tunnels.length; i++) {
          const tunnel = origSite.Tunnels[i];
          const targetServerId = tunnel.isPrimary ? wanChanges.activeServerId : wanChanges.standbyServerId;
  
          const { Server, ...tempTunnel } = tunnel;
  
          tempTunnel.Links = { data: tempTunnel.Links };
          tempTunnel.serverId = targetServerId;
          const updatedTunnel = await Site.updateTunnelPortAndSeed(tempTunnel);
          const tunnelAddressPairSeed = updatedTunnel.tunnelAddressPairSeed;
  
          const tunnelChanges = {
            serverId: targetServerId,
            siteId,
            isClearTextData: !wanChanges.isEncrypted,
            tunnelAddressPairSeed: tunnelAddressPairSeed + index,
          };

          if (tunnel.isPrimary || !removingFailover) {
            await apolloClient.mutate({
              mutation: UPDATE_TUNNEL,
              variables: { id: tunnel.id, changes: tunnelChanges },
            });
          }

          await Gateway.reboot(targetServerId || tunnel.serverId);
        }
  
        if (addingFailover) {
          const { id, Links, Server, ...newTunnel } = origSite.Tunnels[0];
          newTunnel.isPrimary = false;
          newTunnel.serverId = wanChanges.standbyServerId;
          newTunnel.siteId = siteId;
          newTunnel.Links = { data: origSite.Tunnels[0].Links.map(({ id, tunnelId, ...link }) => link) };
          const updatedTunnel = await Site.updateTunnelPortAndSeed(newTunnel);
          updatedTunnel.tunnelAddressPairSeed += index;
  
          await apolloClient.mutate({
            mutation: CREATE_TUNNEL,
            variables: { input: updatedTunnel },
          });
        } 
        
        else if (removingFailover) {
          await GQLHelpers.Models.Link.removeObjects({ apolloClient, options: { variables: { where: { tunnelId: { _eq: origSite.Tunnels[1].id }}}}})
          await GQLHelpers.Models.Tunnel.removeById({ apolloClient, tunnelId: origSite.Tunnels[1].id });
        }
  
        // Update Site
        if (siteLevelAccessChanged) {
          return apolloClient.mutate({
            mutation: UPDATE_SITE,
            variables: {
              id: siteId,
              changes: {
                ipAccessLevel,
                accessibleIps,
              },
            },
          });
        }

        return undefined;
      });
  
      await Promise.all(sitePromises);
    }
    else {
      cognitoUser.signOut();
    }
  },


};
